class PopupProvider {
  open(id, options = {}) {
    throw new Error('open() must be implemented');
  }

  subscribe(id, { onOpen, onClose }) {
    throw new Error('subscribe() must be implemented');
  }

  hasViewed(id) {
    return false;
  }

  getSessionStorageKey() {
    return `popupManagerViewed:${this.state?.provider || 'unknown'}`;
  }

  getSessionViewed() {
    try {
      const raw = sessionStorage.getItem(this.getSessionStorageKey());

      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);

      return parsed && typeof parsed === 'object'
        ? parsed
        : {};
    } catch (error) {
      console.error(
        'Unable to read popup session state:',
        error
      );

      return {};
    }
  }

  markSessionViewed(id) {
    const popupId = String(id);
    const viewed = this.getSessionViewed();

    viewed[popupId] = true;

    try {
      sessionStorage.setItem(
        this.getSessionStorageKey(),
        JSON.stringify(viewed)
      );
    } catch (error) {
      console.error(
        'Unable to save popup session state:',
        error
      );
    }
  }

  hasSessionViewed(id) {
    return Boolean(
      this.getSessionViewed()[String(id)]
    );
  }
}

class KlaviyoPopupProvider extends PopupProvider {
  static activeInstance = null;

  constructor() {
    super();

    this.state = {
      provider: 'klaviyo',
      initialized: true,
      currentPopupId: null,
      isOpen: false,
      lastEvent: null,
    };

    KlaviyoPopupProvider.activeInstance = this;
  }

  static getState() {
    const instance = KlaviyoPopupProvider.activeInstance;

    if (!instance) {
      return null;
    }

    return instance.getState();
  }

  getKlaviyoState() {
    try {
      const raw = localStorage.getItem('klaviyoOnsite');

      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);

      return parsed && typeof parsed === 'object'
        ? parsed
        : {};
    } catch (error) {
      console.error(
        'Unable to read Klaviyo state:',
        error
      );

      return {};
    }
  }

  getViewedForms() {
    const viewedForms =
      this.getKlaviyoState()
        ?.viewedForms
        ?.modal
        ?.viewedForms;

    return viewedForms &&
      typeof viewedForms === 'object'
      ? { ...viewedForms }
      : {};
  }

  hasViewed(id) {
    const formId = String(id);

    return Object.prototype.hasOwnProperty.call(
      this.getViewedForms(),
      formId
    );
  }

  getState() {
    return {
      ...this.state,
      viewedForms: this.getViewedForms(),
      sessionViewedPopups: this.getSessionViewed(),
    };
  }

  subscribe(id, { onOpen, onClose }) {
    const formId = String(id);

    const handler = (event) => {
      const detail = event.detail || {};

      if (String(detail.formId) !== formId) {
        return;
      }

      if (detail.type === 'open') {
        this.state.currentPopupId = formId;
        this.state.isOpen = true;
        this.state.lastEvent = 'open';

        onOpen();
      }

      if (detail.type === 'close') {
        this.state.currentPopupId = formId;
        this.state.isOpen = false;
        this.state.lastEvent = 'close';

        onClose();
      }
    };

    window.addEventListener(
      'klaviyoForms',
      handler
    );

    return () => {
      window.removeEventListener(
        'klaviyoForms',
        handler
      );
    };
  }

  open(id) {
    const formId = String(id);

    this.state.currentPopupId = formId;
    this.state.isOpen = false;
    this.state.lastEvent = 'open_requested';

    window._klOnsite =
      window._klOnsite || [];

    window._klOnsite.push([
      'openForm',
      formId,
    ]);
  }
}

class AliaPopupProvider extends PopupProvider {
  static activeInstance = null;

  static VIEWED_STORAGE_KEY =
    'aliaViewedCampaigns';

  constructor() {
    super();

    this.state = {
      provider: 'alia',
      initialized: true,
      currentPopupId: null,
      isOpen: false,
      lastEvent: null,
    };

    AliaPopupProvider.activeInstance = this;
  }

  static getState() {
    const instance = AliaPopupProvider.activeInstance;

    if (!instance) {
      return null;
    }

    return instance.getState();
  }

  getViewedCampaigns() {
    try {
      const raw = localStorage.getItem(
        AliaPopupProvider.VIEWED_STORAGE_KEY
      );

      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);

      return parsed && typeof parsed === 'object'
        ? parsed
        : {};
    } catch (error) {
      console.error(
        'Unable to read Alia viewed state:',
        error
      );

      return {};
    }
  }

  markViewed(id) {
    const campaignId = String(id);
    const viewed = this.getViewedCampaigns();

    viewed[campaignId] = true;

    try {
      localStorage.setItem(
        AliaPopupProvider.VIEWED_STORAGE_KEY,
        JSON.stringify(viewed)
      );
    } catch (error) {
      console.error(
        'Unable to save Alia viewed state:',
        error
      );
    }
  }

  hasViewed(id) {
    const campaignId = String(id);

    return Boolean(
      this.getViewedCampaigns()[campaignId]
    );
  }

  getState() {
    return {
      ...this.state,
      viewedCampaigns:
        this.getViewedCampaigns(),
      sessionViewedPopups:
        this.getSessionViewed(),
    };
  }

  subscribe(id, { onOpen, onClose }) {
    const campaignId = Number(id);

    const handleOpen = (event) => {
      if (
        Number(event.detail?.campaignID) !==
        campaignId
      ) {
        return;
      }

      this.state.currentPopupId =
        String(id);
      this.state.isOpen = true;
      this.state.lastEvent = 'open';

      this.markViewed(id);

      onOpen();
    };

    const handleClose = (event) => {
      if (
        Number(event.detail?.campaignID) !==
        campaignId
      ) {
        return;
      }

      this.state.currentPopupId =
        String(id);
      this.state.isOpen = false;
      this.state.lastEvent = 'close';

      onClose();
    };

    document.addEventListener(
      'alia:popupView',
      handleOpen
    );

    document.addEventListener(
      'alia:popupClose',
      handleClose
    );

    return () => {
      document.removeEventListener(
        'alia:popupView',
        handleOpen
      );

      document.removeEventListener(
        'alia:popupClose',
        handleClose
      );
    };
  }

  open(id, options = {}) {
    const campaignId = Number(id);

    this.state.currentPopupId =
      String(id);
    this.state.isOpen = false;
    this.state.lastEvent =
      'open_requested';

    window.alia = window.alia || [];

    window.alia.push({
      type: 'open',
      campaignID: campaignId,
      ignoreTargeting:
        options.ignoreTargeting ?? true,
    });
  }
}

class PopupManager {
  constructor(providers = {}) {
    this.providers = providers;
  }

  register(name, provider) {
    this.providers[name] = provider;

    return this;
  }

  emitFallback(provider, popupId, reason) {
    window.dispatchEvent(
      new CustomEvent('popup:fallback', {
        detail: {
          provider,
          popupId: String(popupId),
          reason,
        },
      })
    );
  }

  runAction(action) {
    if (typeof action !== 'function') {
      return;
    }

    Promise.resolve()
      .then(action)
      .catch((error) => {
        console.error(
          'Popup lifecycle action failed:',
          error
        );
      });
  }

  open(
    providerName,
    id,
    {
      onClose = () => {},
      onAlreadyViewed = () => {},
      fallbackAction = () => {},
      timeout = 3000,
      skipIfViewed = false,
      viewedState = 'provider',
      providerOptions = {},
    } = {}
  ) {
    const provider =
      this.providers[providerName];

    if (!provider) {
      this.emitFallback(
        providerName,
        id,
        'unsupported_provider'
      );

      this.runAction(fallbackAction);

      return () => {};
    }

    if (provider.state) {
      provider.state.viewedState =
        viewedState === 'session'
          ? 'session'
          : 'provider';
    }

    /*
     * "Already viewed" is an intentional lifecycle outcome,
     * not a popup failure.
     *
     * It is checked BEFORE subscribe(), timeout creation and
     * provider.open(), so no open_requested state and no
     * popup:fallback telemetry are produced for this path.
     */
    const useSessionViewedState =
      viewedState === 'session';

    const alreadyViewed =
      useSessionViewedState
        ? provider.hasSessionViewed(id)
        : (
            typeof provider.hasViewed === 'function' &&
            provider.hasViewed(id)
          );

    if (
      skipIfViewed &&
      alreadyViewed
    ) {
      if (provider.state) {
        provider.state.currentPopupId =
          String(id);
        provider.state.isOpen = false;
        provider.state.lastEvent =
          'already_viewed';
      }

      this.runAction(onAlreadyViewed);

      return () => {};
    }

    let opened = false;
    let finished = false;
    let timer = null;
    let unsubscribe = () => {};

    const cleanup = () => {
      if (timer !== null) {
        clearTimeout(timer);
      }

      unsubscribe();
    };

    const finish = (action) => {
      if (finished) {
        return;
      }

      finished = true;

      cleanup();
      this.runAction(action);
    };

    unsubscribe = provider.subscribe(id, {
      onOpen: () => {
        if (finished) {
          return;
        }

        opened = true;

        // Session mode is intentionally marked only after the
        // provider confirms that the popup actually opened.
        // open_requested / timeout / open_error never count as viewed.
        if (useSessionViewedState) {
          provider.markSessionViewed(id);
        }

        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      },

      onClose: () => {
        if (!opened || finished) {
          return;
        }

        finish(onClose);
      },
    });

    timer = setTimeout(() => {
      if (opened || finished) {
        return;
      }

      this.emitFallback(
        providerName,
        id,
        'open_timeout'
      );

      finish(fallbackAction);
    }, timeout);

    try {
      provider.open(
        id,
        providerOptions
      );
    } catch (error) {
      console.error(
        `Failed to open popup using "${providerName}":`,
        error
      );

      this.emitFallback(
        providerName,
        id,
        'open_error'
      );

      finish(fallbackAction);
    }

    // Allows manual cancellation of the popup lifecycle.
    return () => {
      finish();
    };
  }
}

const klaviyoPopupProvider =
  new KlaviyoPopupProvider();

const aliaPopupProvider =
  new AliaPopupProvider();

const popupManager = new PopupManager({
  klaviyo: klaviyoPopupProvider,
  alia: aliaPopupProvider,
});

// Optional global exposure for direct use in storefront scripts.
window.PopupProvider = PopupProvider;
window.KlaviyoPopupProvider =
  KlaviyoPopupProvider;
window.AliaPopupProvider =
  AliaPopupProvider;
window.PopupManager = PopupManager;
window.popupManager = popupManager;
